// src/components/job-engine/useJobTypesMeta.ts
import { useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { getJobTypesMeta } from "@/api/jobEngine";
import type { JobTypesMeta } from "@/api/jobEngine";

export interface UseJobTypesMetaResult {
  data: JobTypesMeta | null;
  isLoading: boolean;
  error: string | null;
}

export function useJobTypesMeta(): UseJobTypesMetaResult {
  const [data, setData] = useState<JobTypesMeta | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setIsLoading(true);
    setError(null);
    getJobTypesMeta(ctrl.signal)
      .then((r) => { if (!ctrl.signal.aborted) setData(r); })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (isAxiosError(err)) setError(err.response?.data?.title ?? "Failed to load job types");
        else setError("An unexpected error occurred");
      })
      .finally(() => { if (!ctrl.signal.aborted) setIsLoading(false); });
    return () => ctrl.abort();
  }, []);

  return { data, isLoading, error };
}
